import { generatePath } from "react-router";
import { ROUTES } from "../../../../routes";
import { StepStatus } from "../../ChangeProcess.types";
import { ChangeProcessAccordionItem } from "../../components/ChangeProcessAccordionItem";
import {
  StepNewContract,
  StepStaffReviewOnly,
  StepWithMissingData
} from "../../components/ChangeSteps";
import type { TenantChange, TenantChangeSteps } from "../TenantChanges.types";

interface TenantChangeProcessProps {
  tenantChange: TenantChange;
  isUpdating: boolean;
  shouldShowStaffView: boolean;
  onUpdateStepStatus: (
    operatorChange: TenantChange,
    step: keyof TenantChangeSteps,
    status: StepStatus
  ) => void;
  projectId?: string;
}

function TenantChangeProcess({
  tenantChange,
  isUpdating,
  shouldShowStaffView,
  onUpdateStepStatus,
  projectId
}: TenantChangeProcessProps) {
  const stepNewData = tenantChange.steps.stepNewData;
  const stepNewContract = tenantChange.steps.stepNewContract;
  const stepOldContract = tenantChange.steps.stepOldContract;
  const stepParticipation = tenantChange.steps.stepParticipation;
  const stepMeteringConcept = tenantChange.steps.stepMeteringConcept;

  const disabled = stepParticipation.done === StepStatus.open;

  const newContractLink = projectId
    ? `${generatePath(ROUTES.mieterstrom.contracts, {
        projectId
      })}/neu`
    : undefined;

  return (
    <ChangeProcessAccordionItem
      disabled={isUpdating}
      title={tenantChange.componentName}
      value={`tenant-change-${tenantChange.id}`}
    >
      <StepWithMissingData
        hideSkip
        isUpdating={isUpdating}
        label="Teilnahme am Mieterstrommodell"
        missingData={stepParticipation.missingData}
        modalLink={`mieterwechsel/teilnahme/${tenantChange.id}/`}
        status={stepParticipation.done}
        onUpdateStepStatus={(status) =>
          onUpdateStepStatus(tenantChange, "stepParticipation", status)
        }
      />
      <StepWithMissingData
        disabled={disabled}
        isUpdating={isUpdating}
        label="Stammdaten des neuen Mieters eintragen"
        missingData={stepNewData.missingData}
        modalLink={`mieterwechsel/unternehmensdaten/${tenantChange.id}/`}
        status={stepNewData.done}
        onUpdateStepStatus={(status) =>
          onUpdateStepStatus(tenantChange, "stepNewData", status)
        }
      />
      <StepNewContract
        disabled={disabled}
        isUpdating={isUpdating}
        newContractLink={newContractLink}
        objectsAreMissing={stepNewContract.objectsAreMissing}
        status={stepNewContract.done}
        onUpdateStepStatus={(status) =>
          onUpdateStepStatus(tenantChange, "stepNewContract", status)
        }
      />
      <StepWithMissingData
        disabled={disabled}
        isUpdating={isUpdating}
        label="Verträge des alten Mieters anpassen"
        missingData={stepOldContract.missingData}
        modalLink={`mieterwechsel/vertragsdaten/${tenantChange.id}/`}
        status={stepOldContract.done}
        onUpdateStepStatus={(status) =>
          onUpdateStepStatus(tenantChange, "stepOldContract", status)
        }
      />
      <StepStaffReviewOnly
        disabled={disabled}
        isUpdating={isUpdating}
        label="Messkonzept aktualisieren"
        shouldShowStaffView={shouldShowStaffView}
        status={stepMeteringConcept.done}
        onUpdateStepStatus={(status) =>
          onUpdateStepStatus(tenantChange, "stepMeteringConcept", status)
        }
      />
    </ChangeProcessAccordionItem>
  );
}

export { TenantChangeProcess, TenantChangeProcessProps };
